// src/ReservaModal.js

import React, { useState, useEffect } from 'react';
import { db } from './firebaseConfig';
import { collection, addDoc, doc, updateDoc, query, where, getDocs, orderBy, getDoc, serverTimestamp, limit } from "firebase/firestore";
import { toast } from 'react-toastify';
import HospedeModal from './HospedeModal';

// Imports dos componentes MUI
import {
  Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button, Box, Grid, IconButton,
  List, ListItemButton, ListItemText, Typography, Paper, Chip, Divider, FormControl, FormControlLabel, Radio, RadioGroup,
  ListItem, ListItemIcon, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';


const formatarMoeda = (valor) => {
  return (valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const calcularDuracao = (checkin, checkout) => {
    if (!checkin || !checkout || checkin >= checkout) return 0;
    const dataCheckin = new Date(checkin + 'T00:00:00');
    const dataCheckout = new Date(checkout + 'T00:00:00');
    const diferenca = dataCheckout.getTime() - dataCheckin.getTime();
    return Math.ceil(diferenca / (1000 * 60 * 60 * 24));
};

const verificarConflitos = async (chaletId, dataCheckin, dataCheckout, reservaAtualId = null) => {
    const reservasRef = collection(db, 'reservas');
    const qReservas = query(
      reservasRef,
      where('chaletId', '==', chaletId),
      where('dataCheckin', '<', dataCheckout),
      where('dataCheckout', '>', dataCheckin)
    );
    const queryReservasSnap = await getDocs(qReservas);
    const conflitosReservas = queryReservasSnap.docs
      .filter(doc => doc.id !== reservaAtualId)
      .map(doc => ({ ...doc.data(), tipo: 'reserva' }));

    if (conflitosReservas.length > 0) {
        return conflitosReservas[0];
    }

    const manutencoesRef = collection(db, 'manutencoes');
    const qManutencoes = query(
      manutencoesRef,
      where('chaleId', '==', chaletId),
      where('dataInicio', '<=', dataCheckout),
      where('dataFim', '>=', dataCheckin)
    );
    const queryManutencoesSnap = await getDocs(qManutencoes);
    if (!queryManutencoesSnap.empty) {
        const manutencao = queryManutencoesSnap.docs[0].data();
        return { ...manutencao, tipo: 'manutencao' };
    }

    return null;
};

const gerarProximoCodigoVoucher = async () => {
    const ano = new Date().getFullYear();
    const prefixo = `AABB-${ano}-`;
    
    const reservasRef = collection(db, "reservas");
    const q = query(
        reservasRef,
        where('codigoVoucher', '>=', prefixo),
        where('codigoVoucher', '<', `AABB-${ano+1}-`),
        orderBy('codigoVoucher', 'desc'),
        limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
        return `${prefixo}001`;
    } else {
        const ultimoCodigo = querySnapshot.docs[0].data().codigoVoucher;
        const ultimoNumero = parseInt(ultimoCodigo.split('-')[2], 10);
        const proximoNumero = ultimoNumero + 1;
        const proximoNumeroFormatado = proximoNumero.toString().padStart(3, '0');
        return `${prefixo}${proximoNumeroFormatado}`;
    }
};


const ReservaModal = ({ chale, onClose, checkinPadrao, checkoutPadrao, reservaParaEditar }) => {
  const [checkin, setCheckin] = useState('');
  const [checkout, setCheckout] = useState('');
  const [hospedes, setHospedes] = useState(0);
  const [hospedeSelecionado, setHospedeSelecionado] = useState(null);
  const [acompanhantes, setAcompanhantes] = useState([]);
  const [termoBusca, setTermoBusca] = useState('');
  const [hospedesEncontrados, setHospedesEncontrados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [valorDiaria, setValorDiaria] = useState(0);
  const [numDiarias, setNumDiarias] = useState(0);
  const [valorTotal, setValorTotal] = useState(0);
  const [pacotesDisponiveis, setPacotesDisponiveis] = useState([]);
  const [pacoteAplicado, setPacoteAplicado] = useState(null);
  const [usarPrecoPacote, setUsarPrecoPacote] = useState(true);
  const [tarifasDb, setTarifasDb] = useState(null);
  const [totalDiariaNormal, setTotalDiariaNormal] = useState(0);
  const [hospedeModalOpen, setHospedeModalOpen] = useState(false);
  const [hospedeParaCadastro, setHospedeParaCadastro] = useState(null);

  useEffect(() => {
    const carregarDadosIniciais = async () => {
        if (!chale) return;

        if (reservaParaEditar) {
            // Se estiver a editar, carrega os dados básicos
            setCheckin(reservaParaEditar.dataCheckin);
            setCheckout(reservaParaEditar.dataCheckout);
            setHospedes(reservaParaEditar.numeroHospedes);
            setAcompanhantes(reservaParaEditar.acompanhantes || []);

            // E busca os dados MAIS RECENTES do hóspede para garantir que a categoria (e o preço) esteja atualizada
            try {
                const hospedeRef = doc(db, 'hospedes', reservaParaEditar.hospedeId);
                const hospedeSnap = await getDoc(hospedeRef);
                if (hospedeSnap.exists()) {
                    const hospedeAtualizado = hospedeSnap.data();
                    setHospedeSelecionado({
                        id: reservaParaEditar.hospedeId,
                        nome: hospedeAtualizado.nome,
                        tipoHospede: hospedeAtualizado.tipoHospede, // Usa o tipo mais recente
                    });
                } else {
                    // Fallback para os dados da reserva se o hóspede tiver sido apagado
                    setHospedeSelecionado({
                        id: reservaParaEditar.hospedeId,
                        nome: reservaParaEditar.hospedeNome,
                        tipoHospede: reservaParaEditar.hospedeTipo,
                    });
                    toast.warn("O hóspede original desta reserva não foi encontrado.");
                }
            } catch {
                toast.error("Erro ao buscar os dados atualizados do hóspede.");
                setHospedeSelecionado({
                    id: reservaParaEditar.hospedeId,
                    nome: reservaParaEditar.hospedeNome,
                    tipoHospede: reservaParaEditar.hospedeTipo,
                });
            }
        } else {
            // Se for uma nova reserva, prepara os campos
            setCheckin(checkinPadrao || '');
            setCheckout(checkoutPadrao || '');
            setHospedes(chale.capacidadePadrao || 4);
        }
    };
    carregarDadosIniciais();
  }, [reservaParaEditar, checkinPadrao, checkoutPadrao, chale]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const tarifRef = doc(db, 'tarifas', 'padrao');
        const tarifSnap = await getDoc(tarifRef);
        if (tarifSnap.exists()) setTarifasDb(tarifSnap.data());

        const pacotesRef = collection(db, 'pacotes');
        const q = query(pacotesRef, where("ativo", "==", true));
        const querySnapshot = await getDocs(q);
        const pacotesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        const pacotesAtivosNoPeriodo = pacotesData.filter(p => p.dataInicial && p.dataFinal);
        setPacotesDisponiveis(pacotesAtivosNoPeriodo);

      } catch (error) {
        console.error("Erro ao buscar dados iniciais: ", error);
        toast.error("Erro ao carregar dados de tarifas e pacotes.");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (termoBusca.trim() === '' || hospedeSelecionado) {
      setHospedesEncontrados([]);
      return;
    }
    setBuscando(true);
    const hospedesRef = collection(db, "hospedes");
    const q = query(hospedesRef, where('nome', '>=', termoBusca.toUpperCase()), where('nome', '<=', termoBusca.toUpperCase() + '\uf8ff'), orderBy('nome'));
    const buscar = async () => {
      const querySnapshot = await getDocs(q);
      setHospedesEncontrados(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setBuscando(false);
    };
    const delayDebounceFn = setTimeout(() => { buscar(); }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [termoBusca, hospedeSelecionado]);

  useEffect(() => {
    if (chale) {
        const numAcompanhantes = (hospedes > 0 ? hospedes : chale.capacidadePadrao) - 1;
        setAcompanhantes(Array.from({ length: Math.max(0, numAcompanhantes) }, () => ({ nome: '', cpf: '' })));
    }
  }, [hospedes, chale]);

  useEffect(() => {
    const diarias = calcularDuracao(checkin, checkout);
    setNumDiarias(diarias);

    const pacoteEncontrado = pacotesDisponiveis.find(p => checkin >= p.dataInicial && checkout <= p.dataFinal);
    setPacoteAplicado(pacoteEncontrado || null);
  }, [checkin, checkout, pacotesDisponiveis]);

  useEffect(() => {
    if (!chale) return;

    if (numDiarias > 0 && hospedeSelecionado && tarifasDb) {
      const grupoTarifa = chale.capacidadePadrao === 5 ? 'capacidade_5' : 'capacidade_4';
      const tarifaNormal = tarifasDb[grupoTarifa]?.[hospedeSelecionado.tipoHospede] || 0;
      setValorDiaria(tarifaNormal);
      const totalNormal = numDiarias * tarifaNormal;
      setTotalDiariaNormal(totalNormal);
      
      if (pacoteAplicado && usarPrecoPacote) {
        setValorTotal(pacoteAplicado.valorTotal);
      } else {
        setValorTotal(totalNormal);
      }
    } else {
      setValorDiaria(0);
      setValorTotal(0);
      setTotalDiariaNormal(0);
    }
  }, [numDiarias, hospedeSelecionado, tarifasDb, chale, pacoteAplicado, usarPrecoPacote]);


  if (!chale) {
    return (
        <Dialog open={true} onClose={onClose}>
            <DialogContent><CircularProgress /></DialogContent>
        </Dialog>
    );
  }

  const handleSaveNovoHospede = (hospedeSalvo) => {
    setHospedeSelecionado(hospedeSalvo);
    setHospedeModalOpen(false);
  };

  const handleAcompanhanteChange = (index, event) => {
    const novosAcompanhantes = [...acompanhantes];
    novosAcompanhantes[index][event.target.name] = event.target.value.toUpperCase();
    setAcompanhantes(novosAcompanhantes);
  };
  
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!hospedeSelecionado) {
      toast.warn("Por favor, busque e selecione um hóspede.");
      return;
    }
    if (!checkin || !checkout || checkin >= checkout) {
      toast.warn("Por favor, preencha um período de datas válido.");
      return;
    }

    try {
      const idReservaEditando = reservaParaEditar ? reservaParaEditar.id : null;
      const conflito = await verificarConflitos(chale.id, checkin, checkout, idReservaEditando);

      if (conflito) {
        if (conflito.tipo === 'reserva') {
            const nomeHospede = conflito.hospedeNome;
            const dataCheckinConflito = new Date(conflito.dataCheckin + 'T12:00:00').toLocaleDateString('pt-BR');
            const dataCheckoutConflito = new Date(conflito.dataCheckout + 'T12:00:00').toLocaleDateString('pt-BR');
            toast.error(`CONFLITO: O chalé já está ocupado por ${nomeHospede} entre ${dataCheckinConflito} e ${dataCheckoutConflito}.`, { autoClose: 8000 });
        } else if (conflito.tipo === 'manutencao') {
            const dataInicioConflito = new Date(conflito.dataInicio + 'T12:00:00').toLocaleDateString('pt-BR');
            const dataFimConflito = new Date(conflito.dataFim + 'T12:00:00').toLocaleDateString('pt-BR');
            toast.error(`CONFLITO: O chalé está em manutenção entre ${dataInicioConflito} e ${dataFimConflito}.`, { autoClose: 8000 });
        }
        return;
      }

      const dadosDaReserva = {
        chaletId: chale.id,
        chaleNumero: chale.numero,
        chaleNome: chale.nome,
        dataCheckin: checkin,
        dataCheckout: checkout,
        numeroHospedes: hospedes,
        statusReserva: 'Confirmada',
        hospedeId: hospedeSelecionado.id,
        hospedeNome: hospedeSelecionado.nome,
        hospedeTipo: hospedeSelecionado.tipoHospede,
        acompanhantes: acompanhantes.filter(a => a.nome.trim() !== ''),
        valorDiaria: usarPrecoPacote && pacoteAplicado ? (numDiarias > 0 ? pacoteAplicado.valorTotal / numDiarias : 0) : valorDiaria,
        numDiarias: numDiarias,
        valorTotal: valorTotal,
        pacoteAplicado: pacoteAplicado && usarPrecoPacote ? { id: pacoteAplicado.id, nome: pacoteAplicado.nome } : null,
      };

      if (reservaParaEditar) {
        const reservaRef = doc(db, 'reservas', reservaParaEditar.id);
        await updateDoc(reservaRef, dadosDaReserva);
        toast.success('Reserva atualizada com sucesso!');
      } else {
        const novoCodigoVoucher = await gerarProximoCodigoVoucher();
        const novosDados = {
            ...dadosDaReserva,
            codigoVoucher: novoCodigoVoucher,
            desconto: 0,
            motivoDesconto: '',
            adiantamento: { valor: 0, data: '', metodo: '' },
            pagamentoRestante: { valor: 0, data: '', metodo: '' },
            statusPagamento: 'pendente',
            dataCriacao: serverTimestamp(),
        };
        await addDoc(collection(db, "reservas"), novosDados);
        toast.success(`Reserva criada com sucesso! Voucher: ${novoCodigoVoucher}`);
      }
      onClose();
    } catch (error) {
      console.error("Erro ao salvar reserva: ", error);
      toast.error("Ocorreu um erro ao salvar a reserva.");
    }
  };

  const selecionarHospede = (hospede) => {
    setHospedeSelecionado(hospede);
    setTermoBusca('');
    setHospedesEncontrados([]);
  };

  const abrirModalNovoHospede = () => {
    setHospedeParaCadastro({ nome: termoBusca });
    setHospedeModalOpen(true);
  };

  return (
    <>
      <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {reservaParaEditar ? `Editar Reserva - ${chale.nome}` : `Reservar o ${chale.nome}`}
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="reservaForm" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            
            <Typography variant="h6" gutterBottom>Período da Estadia</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                    <TextField label="Check-in" type="date" required value={checkin} onChange={(e) => setCheckin(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField label="Check-out" type="date" required value={checkout} onChange={(e) => setCheckout(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                </Grid>
                 <Grid item xs={12} sm={4}>
                    <TextField label="Nº de Pessoas" type="number" required value={hospedes} onChange={(e) => setHospedes(parseInt(e.target.value, 10))} inputProps={{ min: 1, max: chale.capacidadeMaxima || chale.capacidadePadrao }} fullWidth />
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Dados do Hóspede Principal</Typography>
            {hospedeSelecionado ? (
              <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography>{hospedeSelecionado.nome}</Typography>
                <Chip label="Alterar" onClick={() => { setHospedeSelecionado(null); setTermoBusca(''); }} />
              </Paper>
            ) : (
              <Box sx={{position: 'relative'}}>
                <TextField
                    label="Buscar Hóspede por nome..."
                    fullWidth
                    value={termoBusca}
                    onChange={e => setTermoBusca(e.target.value)}
                />
                {(buscando || hospedesEncontrados.length > 0 || termoBusca.trim() !== '') && (
                     <Paper sx={{ position: 'absolute', zIndex: 1200, width: '100%', mt: 1 }}>
                        <List>
                            {buscando && <ListItem><ListItemText primary="Buscando..." /></ListItem>}
                            {!buscando && hospedesEncontrados.map(hospede => (
                                <ListItemButton key={hospede.id} onClick={() => selecionarHospede(hospede)}>
                                <ListItemText primary={hospede.nome} secondary={`CPF: ${hospede.cpf || 'N/I'}`} />
                                </ListItemButton>
                            ))}
                            {!buscando && hospedesEncontrados.length === 0 && termoBusca.trim() !== '' && (
                                <ListItemButton onClick={abrirModalNovoHospede} sx={{backgroundColor: '#e3f2fd'}}>
                                    <ListItemIcon><PersonAddIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary={`Nenhum hóspede encontrado. Cadastrar "${termoBusca}"?`} />
                                </ListItemButton>
                            )}
                        </List>
                    </Paper>
                )}
              </Box>
            )}

            {acompanhantes.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>Acompanhantes</Typography>
              <Grid container spacing={2}>
                {acompanhantes.map((acompanhante, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={12} sm={7}>
                      <TextField label={`Nome Acompanhante ${index + 1}`} name="nome" fullWidth value={acompanhante.nome} onChange={(e) => handleAcompanhanteChange(index, e)} inputProps={{ style: { textTransform: 'uppercase' } }} sx={{ mb: 1 }} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField label="CPF (Opcional)" name="cpf" fullWidth value={acompanhante.cpf} onChange={(e) => handleAcompanhanteChange(index, e)} sx={{ mb: 1 }} />
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </>
          )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Financeiro</Typography>
            {pacoteAplicado && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'grey.100' }}>
                <Typography variant="subtitle1" gutterBottom>✨ Pacote encontrado para este período!</Typography>
                <FormControl>
                    <RadioGroup row value={usarPrecoPacote} onChange={(e) => setUsarPrecoPacote(e.target.value === 'true')}>
                    <FormControlLabel value={false} control={<Radio />} label={`Usar Diária Normal (${formatarMoeda(totalDiariaNormal)})`} />
                    <FormControlLabel value={true} control={<Radio />} label={`Aplicar Pacote "${pacoteAplicado.nome}" (${formatarMoeda(pacoteAplicado.valorTotal)})`} />
                    </RadioGroup>
                </FormControl>
                </Paper>
            )}
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={4}><Typography>Total de Diárias:</Typography><Typography variant="h6">{numDiarias} diária(s)</Typography></Grid>
                    <Grid item xs={4}><Typography>Valor da Diária:</Typography><Typography variant="h6">{formatarMoeda(valorDiaria)}</Typography></Grid>
                    <Grid item xs={4}><Typography>Valor Total:</Typography><Typography variant="h5" color="primary" fontWeight="600">{formatarMoeda(valorTotal)}</Typography></Grid>
                </Grid>
            </Paper>

          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={onClose} variant="outlined" color="inherit">Cancelar</Button>
          <Button type="submit" form="reservaForm" variant="contained" color="primary" size="large">
            {reservaParaEditar ? 'Salvar Alterações' : 'Confirmar Reserva'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <HospedeModal 
        open={hospedeModalOpen}
        onClose={() => setHospedeModalOpen(false)}
        onSave={handleSaveNovoHospede}
        hospedeParaEditar={hospedeParaCadastro}
      />
    </>
  );
};

export default ReservaModal;

